<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { pmaSvg, growSvg } from '@/util/icons'
import { capitalize } from '@/util/strings'
import { useVisibilityStore } from '@/visibility/visibility.store'

const props = defineProps({
  goal_resources: {
    type: Array<string>,
    required: false,
    default: () => {
      return []
    }
  },
  resource_languages: {
    type: Array<string>,
    required: false,
    default: () => {
      return []
    }
  },
  show_grow: {
    type: Boolean,
    required: false,
    default: false
  }
})
const visibilityStore = useVisibilityStore()
const { betaVisibility } = storeToRefs(visibilityStore)

const showProgress = computed(() => {
  return props.goal_resources.length > 0
})
const hasNonEnglishLanguages = computed(() => {
  return props.resource_languages.some((lang: string) => lang != 'english')
})

const iconLabel = computed(() => {
  let labelPieces = []
  if (hasNonEnglishLanguages.value) {
    labelPieces.push('Has assessments in multiple languages, including ' + formattedLanguages.value + '.')
  }
  if (showProgress.value) {
    labelPieces.push('Has progress monitoring assessments and data sheets.')
  }
  if (props.show_grow && betaVisibility.value) {
    labelPieces.push('This goal can be tracked in Goalbook Grow if saved to the Library.')
  }
  return labelPieces.join(' ')
})

const progressTooltip = computed(() => {
  return `<div class='flex items-start'><span class="pr-2">${pmaSvg}</span> <p>Progress monitoring assessments and data sheets</p></div>`
})
const progressAndLanguageTooltip = computed(() => {
  return `<div class='flex items-start pt-2'><span class="material-icons-round pr-1 text-lg"> public </span> <p>Includes assessments in <b>${formattedLanguages.value}</b></p></div>`
})
const growTooltip = computed(() => {
  return `<div class='tooltip-inner-container flex align-center pt-2 items-start'><span class='w-6 mr-1'>${growSvg}</span> <p>Can be tracked in Goalbook Grow if saved to the Library</p></div>`
})

const getTooltipContent = computed(() => {
  let tooltipContent = ''
  if (showProgress.value) {
    tooltipContent += progressTooltip.value
  }
  if (hasNonEnglishLanguages.value) {
    tooltipContent += progressAndLanguageTooltip.value
  }
  if (props.show_grow && betaVisibility.value) {
    tooltipContent += growTooltip.value
  }
  return tooltipContent
})

const formattedLanguages = computed(() => {
  return props.resource_languages
    ?.reduce((filtered: string[], lang) => {
      if (lang !== 'english') {
        filtered.push(capitalize(lang))
      }
      return filtered
    }, [])
    .join(', ')
})
</script>
<template>
  <div
    class="flex items-center justify-between"
    v-if="showProgress || hasNonEnglishLanguages || (props.show_grow && betaVisibility)"
    tabindex="0"
    v-tooltip.bottom="{
      value: getTooltipContent,
      escape: false, 
    }"
    :aria-label="iconLabel"
  >
    <span v-if="hasNonEnglishLanguages" class="material-icons-round pr-1 text-lg"> public </span>
    <span v-if="showProgress" v-html="pmaSvg" />
    <span v-if="props.show_grow && betaVisibility" v-html="growSvg" class="w-4 ml-[5px] -mr-2"/>
  </div>
</template>
<style>
.tooltip-inner-container {
  svg {
    width: 15px;
  }
}
</style>